import React from 'react'
import { DeclarationStatusEnum } from '../../../types/IDeclaration'
import { DeclarationAuditStatus } from '../../../types/DeclarationAudit'

interface TableRowStatusProps {
  value: string
  // Editor surfaces render the full coloured Figma badge; everywhere else
  // (lists, preview, sidebar) keeps the lighter coloured-text treatment.
  asBadge?: boolean
}

type StatusBadgeVariant = 'success' | 'error' | 'warning' | 'neutral'

const VARIANT_TEXT_CLASS: Record<StatusBadgeVariant, string> = {
  success: 'text-success',
  error: 'text-danger',
  warning: 'text-warning',
  neutral: 'text-muted',
}

interface ResolvedStatus {
  label: string
  variant: StatusBadgeVariant
}

function resolveStatus(value: string): ResolvedStatus {
  switch (value) {
    case DeclarationStatusEnum.OPEN.toString():
      return { label: 'TRANSIT OPEN', variant: 'success' }
    case DeclarationStatusEnum.DRAFT.toString():
      return { label: 'DRAFT', variant: 'neutral' }
    case DeclarationStatusEnum.ACCEPTED.toString():
      return { label: 'ACCEPTED', variant: 'success' }
    case DeclarationStatusEnum.CUSTOMS_REQUEST.toString():
      return { label: 'CUSTOMS REQUEST', variant: 'error' }
    case DeclarationStatusEnum.CANCELED.toString():
      return { label: 'CANCELED', variant: 'error' }
    case DeclarationStatusEnum.CANCEL_REQUESTED.toString():
      return { label: 'CANCEL REQUESTED', variant: 'warning' }
    case DeclarationStatusEnum.AMENDMENT_REQUEST.toString():
      return { label: 'AMENDMENT REQUEST', variant: 'warning' }
    case DeclarationStatusEnum.CONTROL_DECISION.toString():
      return { label: 'CONTROL DECISION', variant: 'neutral' }
    case DeclarationStatusEnum.UNLOADING_PERMISSION.toString():
      return { label: 'UNLOADING PERMISSION', variant: 'neutral' }
    case DeclarationStatusEnum.WRITEOFF_NOTIFICATION.toString():
      return { label: 'WRITEOFF NOTIFICATION', variant: 'neutral' }
    case DeclarationStatusEnum.GOODS_RELEASE_NOTIFICATION.toString():
      return { label: 'GOODS RELEASE NOTIFICATION', variant: 'neutral' }
    case DeclarationStatusEnum.ASSESSED.toString():
      return { label: 'ASSESSED', variant: 'success' }
    case DeclarationStatusEnum.BOOKING.toString():
      return { label: 'BOOKING', variant: 'warning' }
    case DeclarationStatusEnum.CANCELLATION_OK.toString():
      return { label: 'CANCELLATION OK', variant: 'neutral' }
    case DeclarationStatusEnum.CANCELLED.toString():
      return { label: 'CANCELLED', variant: 'error' }
    case DeclarationStatusEnum.CAN_RELEASE.toString():
      return { label: 'CAN RELEASE', variant: 'neutral' }
    case DeclarationStatusEnum.CHECK_NOT_OK.toString():
      return { label: 'CHECK NOT OK', variant: 'error' }
    case DeclarationStatusEnum.DELETED.toString():
      return { label: 'DELETED', variant: 'neutral' }
    case DeclarationStatusEnum.DISCREPANCIES.toString():
      return { label: 'DISCREPANCIES', variant: 'error' }
    case DeclarationStatusEnum.GOODS_RELEASED.toString():
      return { label: 'GOODS RELEASED', variant: 'success' }
    case DeclarationStatusEnum.IE006_ARRIVED.toString():
      return { label: 'GOODS ARRIVED', variant: 'success' }
    case DeclarationStatusEnum.INVALID_CANCELLATION.toString():
      return { label: 'INVALID CANCELLATION', variant: 'error' }
    case DeclarationStatusEnum.NOT_FOUND.toString():
      return { label: 'NOT FOUND', variant: 'error' }
    case DeclarationStatusEnum.PROCEDURE_ENDED.toString():
      return { label: 'PROCEDURE ENDED', variant: 'success' }
    case DeclarationStatusEnum.RECOVERY.toString():
      return { label: 'RECOVERY', variant: 'warning' }
    case DeclarationStatusEnum.RECOVERY_COMPLETED.toString():
      return { label: 'RECOVERY COMPLETED', variant: 'success' }
    case DeclarationStatusEnum.REDIRECTED.toString():
      return { label: 'REDIRECTED', variant: 'warning' }
    case DeclarationStatusEnum.TRANSIT_FORBIDDEN.toString():
      return { label: 'TRANSIT FORBIDDEN', variant: 'warning' }
    case DeclarationStatusEnum.TRANSIT_PROCEDURE_COMPLETED.toString():
      return { label: 'TRANSIT ENDED', variant: 'neutral' }
    case DeclarationStatusEnum.T1_TRANSIT_CLOSED.toString():
      return { label: 'TRANSIT ENDED', variant: 'neutral' }
    case DeclarationStatusEnum.WAITING_CANCELLATION.toString():
      return { label: 'WAITING CANCELLATION', variant: 'neutral' }
    case DeclarationStatusEnum.WAITING_GOODS.toString():
      return { label: 'WAITING GOODS', variant: 'neutral' }
    case DeclarationStatusEnum.REJECTED.toString():
      return { label: 'REJECTED', variant: 'error' }
    case DeclarationStatusEnum.DECLINED.toString():
      return { label: 'DECLINED', variant: 'error' }
    case DeclarationStatusEnum.IN_REVIEW.toString():
      return { label: 'IN REVIEW', variant: 'warning' }
    case DeclarationStatusEnum.CHANGE_REVIEW.toString():
      return { label: 'CHANGE REVIEW', variant: 'warning' }
    case DeclarationStatusEnum.NON_ARRIVED_MOVEMENT.toString():
      return { label: 'NON-ARRIVED MOVEMENT', variant: 'error' }
    case DeclarationStatusEnum.NON_ARRIVED_MOVEMENT_RELEASE_REQUEST.toString():
      return { label: 'NON-ARRIVED MOVEMENT RELEASE REQUEST', variant: 'warning' }
    case DeclarationAuditStatus.PROHIBITED_DESCRIPTION.toString():
      return { label: 'PROHIBITED DESCRIPTION', variant: 'error' }
    default:
      return { label: value ? value.replaceAll('_', ' ') : value, variant: 'success' }
  }
}

function TableRowStatus({ value, asBadge = false }: TableRowStatusProps) {
  const { label, variant } = resolveStatus(value)

  if (asBadge) {
    return <span className={`status-badge status-badge--${variant}`}>{label.toLowerCase()}</span>
  }

  return <span className={VARIANT_TEXT_CLASS[variant]}>{label}</span>
}

TableRowStatus.defaultProps = {
  asBadge: false,
}

export default TableRowStatus
